import { useState, useEffect } from 'react';

/**
 * usePWA — Hook para manejar la instalación de la PWA y las actualizaciones
 * 
 * Cómo usarlo en un componente:
 *   const { isInstallable, installApp, updateAvailable, applyUpdate } = usePWA();
 */
export function usePWA() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    // 1. Capturamos el evento nativo del navegador para mostrar el banner de instalación
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); // Evitamos que el banner aparezca solo
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 2. Detectamos cuando hay una nueva versión del Service Worker esperando
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        // Si ya hay uno esperando al momento de cargar (ej: regresó de offline)
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setUpdateAvailable(true);
        }

        // Escuchamos futuros updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // Nuevo SW instalado, el viejo sigue corriendo → notificar al usuario
              setWaitingWorker(newWorker);
              setUpdateAvailable(true);
            }
          });
        });
      });

      // 3. Recargamos la página cuando el nuevo SW toma el control
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  /**
   * Muestra el diálogo nativo del navegador para instalar la app
   */
  const installApp = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstallable(false);
      setInstallPrompt(null);
    }
  };

  /**
   * Le dice al Service Worker que está esperando que tome el control ahora,
   * lo que dispara un 'controllerchange' y recarga la página con la versión nueva.
   */
  const applyUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  };

  return { isInstallable, installApp, updateAvailable, applyUpdate };
}
